import { useCallback, useEffect, useState } from 'react';
import { CATEGORIES, displayTypeLabel, SOURCE_LANG, VIEW_LANGS } from './constants';
import { deleteExploreItem, getExploreItems, translateExploreItem } from './api';
import { describeI18nStatus } from './i18nStatus';

const InfoChip = ({ children }) => (
  <span className="px-1.5 py-0.5 rounded-md bg-dcp-surface-variant text-dcp-text-2 text-[10px] font-semibold">
    {children}
  </span>
);

const TONE_CLASS = {
  ok: 'bg-dcp-primary/15 text-dcp-primary',
  warn: 'bg-amber-500/15 text-amber-500',
  busy: 'bg-dcp-surface-variant text-dcp-text-2',
  error: 'bg-dcp-error/15 text-dcp-error',
};

const I18nChip = ({ status, onRetry }) => {
  const { label, tone, canRetry } = describeI18nStatus(status);
  if (tone === 'none') return null;
  return (
    <button
      type="button"
      disabled={!canRetry}
      onClick={(e) => {
        e.stopPropagation();
        onRetry();
      }}
      title={canRetry ? 'Çeviriyi yeniden başlat' : undefined}
      className={`px-1.5 py-0.5 rounded-md text-[10px] font-semibold ${TONE_CLASS[tone]} ${
        canRetry ? 'hover:opacity-80' : 'cursor-default'
      }`}
    >
      {label}
    </button>
  );
};

export default function AdminList({ category, onSelectCategory, onCreate, onEdit, onLogout }) {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [viewLang, setViewLang] = useState(SOURCE_LANG);

  // Turkce disi her dil SALT OKUNUR. Form hangi dili yuklediyse onu geri
  // yaziyor; Almanca gorunumde kaydetmek kanonik Turkce'yi ezerdi.
  const isPreview = viewLang !== SOURCE_LANG;

  const loadItems = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      setItems(await getExploreItems(category, 100, viewLang));
    } catch (err) {
      setItems([]);
      setError(err.message);
    }
    setIsLoading(false);
  }, [category, viewLang]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const retryTranslation = async (id) => {
    try {
      const { item } = await translateExploreItem(id);
      setItems((prev) => prev.map((i) => (i._id === id ? { ...i, i18nStatus: item.i18nStatus } : i)));
    } catch (err) {
      setError(err.message);
    }
  };

  const confirmDelete = async () => {
    const item = pendingDelete;
    setPendingDelete(null);
    try {
      await deleteExploreItem(item._id);
      setItems((prev) => prev.filter((i) => i._id !== item._id));
    } catch (err) {
      setError(err.message);
    }
  };

  const activeCategory = CATEGORIES.find((c) => c.key === category);

  return (
    <div className="min-h-screen bg-dcp-bg">
      <header className="sticky top-0 z-10 bg-dcp-bg border-b border-dcp-border">
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex items-center h-16">
            <h1 className="flex-1 text-dcp-text font-bold text-lg">Admin Panel</h1>
            <button
              onClick={loadItems}
              className="text-dcp-text-2 text-sm px-3 py-1.5 rounded-lg hover:bg-dcp-surface"
            >
              Yenile
            </button>
            <button
              onClick={onLogout}
              className="text-dcp-text-2 text-sm px-3 py-1.5 rounded-lg hover:bg-dcp-surface"
            >
              Çıkış
            </button>
          </div>

          <div className="flex gap-2 overflow-x-auto pb-3">
            {CATEGORIES.map((cat) => {
              const selected = cat.key === category;
              return (
                <button
                  key={cat.key}
                  onClick={() => onSelectCategory(cat.key)}
                  title={cat.description}
                  className={`shrink-0 px-3.5 py-1.5 rounded-full border text-[13px] font-semibold transition-colors ${
                    selected
                      ? 'bg-dcp-primary border-dcp-primary text-dcp-on-primary'
                      : 'bg-dcp-surface border-dcp-border text-dcp-text hover:border-dcp-border-dark'
                  }`}
                >
                  {cat.label}
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-2 pb-3">
            <span className="text-dcp-text-2 text-[11px] font-semibold shrink-0">Dil</span>
            {VIEW_LANGS.map((lang) => {
              const selected = lang.key === viewLang;
              return (
                <button
                  key={lang.key}
                  onClick={() => setViewLang(lang.key)}
                  className={`px-2.5 py-1 rounded-lg border text-[11px] font-bold transition-colors ${
                    selected
                      ? 'bg-dcp-primary border-dcp-primary text-dcp-on-primary'
                      : 'bg-dcp-surface border-dcp-border text-dcp-text-2 hover:border-dcp-border-dark'
                  }`}
                >
                  {lang.label}
                </button>
              );
            })}
          </div>

          {isPreview && (
            <p className="pb-3 text-amber-500 text-[12px]">
              Çeviri önizlemesi — salt okunur. Düzenlemek için TR'ye dön.
            </p>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-4 pb-28">
        {activeCategory && (
          <p className="text-dcp-text-3 text-xs mb-4">{activeCategory.description}</p>
        )}

        {isLoading ? (
          <p className="text-center text-dcp-text-2 py-20">Yükleniyor…</p>
        ) : items.length === 0 ? (
          <p className="text-center text-dcp-text-2 py-20">
            {error ?? 'Bu kategoride henüz içerik yok.'}
          </p>
        ) : (
          <ul className="space-y-2">
            {items.map((item) => (
              <li key={item._id}>
                <div
                  role={isPreview ? undefined : 'button'}
                  tabIndex={isPreview ? undefined : 0}
                  onClick={isPreview ? undefined : () => onEdit(item)}
                  onKeyDown={isPreview ? undefined : (e) => e.key === 'Enter' && onEdit(item)}
                  className={`flex items-center gap-3 p-3 rounded-2xl bg-dcp-surface border border-dcp-border transition-colors ${
                    isPreview ? '' : 'hover:border-dcp-border-dark cursor-pointer'
                  }`}
                >
                  <div className="shrink-0 w-14 h-14 rounded-[10px] overflow-hidden bg-dcp-surface-variant">
                    {item.thumbnailUrl && (
                      <img
                        src={item.thumbnailUrl}
                        alt=""
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="text-dcp-text font-semibold text-[15px] truncate">{item.title}</p>
                    <div className="flex gap-1.5 mt-1">
                      <InfoChip>
                        {item.spanX ?? 1}x{item.spanY ?? 1}
                      </InfoChip>
                      <InfoChip>{displayTypeLabel(item.displayType ?? 'image')}</InfoChip>
                      <InfoChip>#{item.order ?? 0}</InfoChip>
                      {item.isActive === false && <InfoChip>Pasif</InfoChip>}
                      {item.isPremium && <InfoChip>Premium</InfoChip>}
                      <I18nChip
                        status={item.i18nStatus}
                        onRetry={() => retryTranslation(item._id)}
                      />
                    </div>
                  </div>

                  {!isPreview && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setPendingDelete(item);
                      }}
                      className="shrink-0 w-9 h-9 rounded-full text-dcp-error hover:bg-dcp-surface-variant"
                      aria-label="Sil"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        {error && items.length > 0 && (
          <p className="mt-4 text-center text-dcp-error text-sm">{error}</p>
        )}
      </main>

      {!isPreview && (
        <button
          onClick={onCreate}
          className="fixed bottom-8 right-8 w-14 h-14 rounded-full bg-dcp-primary text-dcp-on-primary text-3xl leading-none shadow-lg hover:bg-dcp-primary-dark transition-colors"
          aria-label="Yeni içerik"
        >
          +
        </button>
      )}

      {pendingDelete && (
        <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/60 px-4">
          <div className="w-full max-w-sm rounded-2xl bg-dcp-surface border border-dcp-border p-6">
            <h2 className="text-dcp-text font-bold text-lg">Silme Onayı</h2>
            <p className="text-dcp-text-2 text-sm mt-2">
              "{pendingDelete.title}" silinsin mi?
            </p>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setPendingDelete(null)}
                className="px-4 py-2 rounded-lg text-dcp-text-2 text-sm font-semibold hover:bg-dcp-surface-variant"
              >
                İptal
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 rounded-lg text-dcp-error text-sm font-semibold hover:bg-dcp-surface-variant"
              >
                Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
